import { axiosInstance } from '@/lib/axios';
import type {
  PermitSearchRequest,
  PermitSearchResponse,
  PermitSearchDetailResponse,
  PermitSearchSummaryResponse,
  PermitSearchHistoryResponse,
  FeeCalculationRequest,
  PermitCalculationRequest,
  PermitCalculationResponse,
  ScopeCategoriesResponse,
  StatesResponse,
  CitiesResponse,
} from '@/types/api.types';

/**
 * Permits Service
 * Handles all permit-related API calls
 */

/**
 * Get available states with municipality data
 */
export const getAvailableStates = async (): Promise<StatesResponse> => {
  const response = await axiosInstance.get<StatesResponse>('/permits/states');
  return response.data;
};

/**
 * Get cities for a specific state
 */
export const getCitiesForState = async (state: string): Promise<CitiesResponse> => {
  const response = await axiosInstance.get<CitiesResponse>('/permits/cities', {
    params: { state },
  });
  return response.data;
};

/**
 * Search cities by name within a state
 */
export const searchCities = async (
  state: string,
  query: string
): Promise<CitiesResponse> => {
  const response = await axiosInstance.get<CitiesResponse>('/permits/cities/search', {
    params: { state, query },
  });
  return response.data;
};

/**
 * Get available project scope categories
 */
export const getScopeCategories = async (): Promise<ScopeCategoriesResponse> => {
  const response = await axiosInstance.get<ScopeCategoriesResponse>(
    '/permits/scope-categories'
  );
  return response.data;
};

/**
 * Calculate permit fees for a project (main endpoint)
 */
export const calculatePermitFeesNew = async (
  request: PermitCalculationRequest
): Promise<PermitCalculationResponse> => {
  const response = await axiosInstance.post<PermitCalculationResponse>(
    '/permits/calculate-fees',
    request
  );
  return response.data;
};

/**
 * Search for permit information by city, state, and job description (legacy)
 */
export const searchPermits = async (
  searchParams: PermitSearchRequest
): Promise<PermitSearchResponse> => {
  const response = await axiosInstance.post<PermitSearchResponse>(
    '/permits/search',
    searchParams
  );
  return response.data;
};

/**
 * Get permit search details by ID
 */
export const getPermitById = async (
  searchId: string
): Promise<PermitSearchDetailResponse> => {
  const response = await axiosInstance.get<PermitSearchDetailResponse>(
    `/permits/${searchId}`
  );
  return response.data;
};

/**
 * Calculate permit fees based on job details (legacy)
 */
export const calculatePermitFees = async (
  permitId: string,
  calculation: FeeCalculationRequest
): Promise<any> => {
  const response = await axiosInstance.post(
    `/permits/${permitId}/calculate-fees`,
    calculation
  );
  return response.data;
};

/**
 * Get user's recent permit searches
 */
export const getRecentSearches = async (limit = 10): Promise<PermitSearchSummaryResponse[]> => {
  const response = await axiosInstance.get<PermitSearchSummaryResponse[]>('/permits/recent', {
    params: { limit },
  });
  return response.data;
};

/**
 * Get user's paginated permit search history
 */
export const getPermitHistory = async (
  limit = 20,
  offset = 0
): Promise<PermitSearchHistoryResponse> => {
  const response = await axiosInstance.get<PermitSearchHistoryResponse>('/permits/history', {
    params: { limit, offset },
  });
  return response.data;
};

/**
 * Save a permit search for later reference
 */
export const savePermitSearch = async (permitId: string): Promise<void> => {
  await axiosInstance.post(`/permits/${permitId}/save`);
};

/**
 * Get user's saved permit searches
 */
export const getSavedSearches = async (): Promise<PermitSearchResponse[]> => {
  const response = await axiosInstance.get<PermitSearchResponse[]>('/permits/saved');
  return response.data;
};

/**
 * Remove a saved permit search
 */
export const removeSavedSearch = async (permitId: string): Promise<void> => {
  await axiosInstance.delete(`/permits/${permitId}/save`);
};

/**
 * Export permit information as PDF
 */
export const exportPermitPDF = async (permitId: string): Promise<Blob> => {
  const response = await axiosInstance.get(`/permits/${permitId}/export/pdf`, {
    responseType: 'blob',
  });
  return response.data;
};

/**
 * Share permit information via email
 */
export const sharePermitByEmail = async (permitId: string, email: string): Promise<void> => {
  await axiosInstance.post(`/permits/${permitId}/share`, { email });
};
